import { Listbox, Transition } from '@headlessui/react';
import Image from 'next/future/image';
import { Fragment, useRef, useState } from 'react';
import cn from 'classnames';
import downIcon from '@/public/icons/collection/chevron_down.svg';
import { useOutsideClickHandler } from '@/lib/common/use-outside-click-handler';
import { SortDropdownProps, SortType } from './types';
import Radio from './Radio';

const SortDropdown = (props: SortDropdownProps) => {
  const { sortList, sortSelected, onSortChange, isLoading } = props;
  const containerRef = useRef(null);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useOutsideClickHandler(containerRef, () => setIsDropdownOpen(false));

  return (
    <Listbox
      value={sortSelected}
      onChange={(sort: SortType) => {
        setIsDropdownOpen(false);
        onSortChange(sort);
      }}
      as="div"
      ref={containerRef}
      className={cn(
        'relative z-20',
        {
          'pointer-events-none opacity-30': isLoading,
        },
        {
          'opacity-100': !isLoading,
        },
      )}
    >
      <Listbox.Button
        className="flex h-full w-full items-center justify-center gap-x-3 px-3 lg:justify-between"
        onClick={(e: React.MouseEvent) => {
          e.preventDefault();
          setIsDropdownOpen((prevState) => !prevState);
        }}
      >
        <span
          className={cn('block truncate', {
            'text-left': isDropdownOpen,
          })}
        >
          {sortSelected.name}
        </span>
        <span
          className={cn({
            'rotate-180': isDropdownOpen,
          })}
        >
          <div className="relative h-[6px] w-[12px] xs:h-[7px] xs:w-[14px]">
            <Image
              src={downIcon}
              loading="eager"
              alt="down"
              className="w-full object-contain"
            />
          </div>
        </span>
      </Listbox.Button>
      <Transition as={Fragment} show={isDropdownOpen}>
        <Listbox.Options
          className="absolute w-full rounded border bg-white"
          style={{
            boxShadow: '0px 10px 20px -15px rgba(0,0,0,0.1)',
            WebkitBoxShadow: '0px 15px 20px -10px rgba(0,0,0,0.1)',
          }}
        >
          {sortList.map((sort) => (
            <Listbox.Option
              key={sort.value}
              value={sort}
              as="button"
              className="group relative w-full py-2 text-left hover:underline"
            >
              {({ selected }) => (
                <>
                  <Radio
                    isChecked={sort.value === sortSelected.value}
                    positionClass="left-4 top-1/2"
                  />
                  <span
                    className={`ml-9 block truncate ${
                      selected ? 'font-medium' : 'font-normal'
                    }`}
                  >
                    {sort.name}
                  </span>
                </>
              )}
            </Listbox.Option>
          ))}
        </Listbox.Options>
      </Transition>
    </Listbox>
  );
};

export default SortDropdown;
